import React from 'react';
import moment from 'moment';
import EventCardContent from '../shared/EventCardContent';
import { APPRAISER_EVENT_STAGE } from '../../constants/AppraiserEventStage';
import cx from 'classnames';
import { getFirstDayOfTheWeek, getToday } from '@shared/services/dateService';
import { useNavigate } from 'react-router-dom';
import { DELIVERY_METHODS } from '../../constants/DeliveryMethodsType';
import { getTooltipContent } from '../shared/TooltipContentProvider';

const WEEK_DAYS_COUNT = 7;

/**
 * @typedef {object} WeeklyCalendarEvents
 * @property {string} [OrgUnitNames]
 * @property {string} [AtlName]
 * @property {string} [StartDate]
 * @property {string} [EndDate]
 * @property {*} [Stage]
 * @property {number} [Id]
 * @property {Array<*>} [WarningTypes]
 * @property {number|string} [DeliveryMethod]
 * @property {boolean} [IsAtlAssignedStage]
 */

/**
 * @typedef {object} WeeklyCalendarProps
 * @property {Date} [selectedDate]
 * @property {boolean} [isWeekUnavailableForOrgUnit]
 * @property {WeeklyCalendarEvents[]} events
 * @property {boolean} [cantAccessPastEvents]
 * @property {number} [selectedAtmUser]
 * @property {boolean} [hideBidWarnings]
 * @property {boolean} [checkAtmAccessibility]
 */

/**
 * @param {WeeklyCalendarProps} props
 */
const WeeklyCalendar = props => {
  const {
    events,
    selectedDate,
    isWeekUnavailableForOrgUnit = false,
    cantAccessPastEvents = false,
    selectedAtmUser,
    hideBidWarnings = false,
    checkAtmAccessibility = false
  } = props;

  const today = getToday();

  const navigate = useNavigate();

  const startOfWeek = getFirstDayOfTheWeek(selectedDate || today);
  const weekDays = Array.from({ length: WEEK_DAYS_COUNT }, (_, i) =>
    moment(startOfWeek).clone().add(i, 'days').format('D ddd')
  );

  const isPastEvent = eventStartDate => {
    return cantAccessPastEvents && moment(eventStartDate).isSameOrBefore(getToday(), 'day');
  };

  const getCustomCss = event => {
    const eventStart = moment(event.StartDate);
    const eventEnd = moment(event.EndDate);
    const offset = Math.max(eventStart.diff(startOfWeek, 'days'), 0);
    const length = Math.min(
      eventEnd.diff(startOfWeek, 'days') + 1 - offset,
      WEEK_DAYS_COUNT - offset
    );

    return {
      left: `${(offset / WEEK_DAYS_COUNT) * 100}%`,
      width: `${(length / WEEK_DAYS_COUNT) * 100}%`
    };
  };

  const isEventDisabled = event => {
    if (isPastEvent(event.StartDate)) {
      return true;
    }

    if (!checkAtmAccessibility) {
      return isWeekUnavailableForOrgUnit;
    }

    const { IsEventAtm, IsPotentialAtm, IsUserInvitedAtm } = event.TargetUserEventParticipationInfo;

    return !IsEventAtm && !IsPotentialAtm && !IsUserInvitedAtm;
  };

  const handleOnClickEvent = event => {
    if (isEventDisabled(event)) {
      return;
    }
    const path = `/calendar/edit-event/${event.Id}`;
    selectedAtmUser !== undefined ? navigate(path, { state: { selectedAtmUser } }) : navigate(path);
  };

  return (
    <div className='weekly-calendar'>
      <div className='calendar-days'>
        {weekDays.map(day => (
          <div key={`header-cell-${day}`} className='header-cell'>
            <div className='header-text bold'>{day}</div>
          </div>
        ))}
      </div>
      <div className='events scrollBar'>
        {events.map(event => {
          const deliveryMethod = Object.values(DELIVERY_METHODS).find(
            x => x.value === event.DeliveryMethod
          );

          const eventClasses = {
            'event-card': true,
            draft: event.Stage === APPRAISER_EVENT_STAGE.DRAFT,
            'atl-assigned': event.IsAtlAssignedStage,
            'open-for-bid': event.Stage === APPRAISER_EVENT_STAGE.TEAM_REVIEW_OPEN_FOR_BID,
            'with-delivery-method': !!deliveryMethod,
            'team-assigned': event.Stage === APPRAISER_EVENT_STAGE.TEAM_ASSIGNED,
            'ready-to-deliver': event.Stage === APPRAISER_EVENT_STAGE.READY_TO_DELIVER,
            unavailable: isEventDisabled(event)
          };

          return (
            <div
              key={`event-card-${event.Id}`}
              className={cx(eventClasses)}
              title={getTooltipContent(event, deliveryMethod)}
              style={getCustomCss(event)}
              onClick={() => handleOnClickEvent(event)}>
              <EventCardContent
                eventId={event.Id}
                atlName={event.AtlName}
                orgUnitNames={event.OrgUnitNames}
                deliveryMethod={deliveryMethod ? deliveryMethod.label : null}
                warningTypes={event.WarningTypes}
                hideBidWarnings={hideBidWarnings}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyCalendar;
